<script setup>
import { ref, computed } from 'vue';
import { NodeViewWrapper } from '@tiptap/vue-3';

const props = defineProps({
    node: { type: Object, required: true },
    updateAttributes: { type: Function, required: true },
    selected: { type: Boolean, default: false },
    editor: { type: Object, required: true },
});

const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

const width = computed(() => props.node.attrs.width || null);
const align = computed(() => props.node.attrs.align || 'left');

const wrapperStyle = computed(() => {
    const map = { left: 'flex-start', center: 'center', right: 'flex-end' };
    return { justifyContent: map[align.value] ?? 'flex-start' };
});

function onResizeStart(event) {
    if (!props.editor.isEditable) return;
    event.preventDefault();
    event.stopPropagation();

    isResizing.value = true;
    startX.value = event.clientX;

    const img = event.target.closest('.image-node-view__inner').querySelector('img');
    startWidth.value = img.getBoundingClientRect().width;

    const onMove = (e) => {
        const dx = e.clientX - startX.value;
        const newWidth = Math.max(80, Math.round(startWidth.value + dx));
        props.updateAttributes({ width: newWidth });
    };

    const onUp = () => {
        isResizing.value = false;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
}
</script>

<template>
    <NodeViewWrapper
        class="image-node-view"
        :class="{ 'image-node-view--resizing': isResizing }"
        :style="wrapperStyle"
    >
        <div
            class="image-node-view__inner"
            :class="{ 'image-node-view__inner--selected': selected }"
        >
            <img
                :src="node.attrs.src"
                :alt="node.attrs.alt || ''"
                :title="node.attrs.title || ''"
                :style="width ? { width: width + 'px', height: 'auto' } : { maxWidth: '100%' }"
                draggable="false"
            />
            <div v-if="editor.isEditable" class="image-node-view__handle" @mousedown="onResizeStart" />
        </div>
    </NodeViewWrapper>
</template>

<style scoped>
/* NodeViewWrapper ocupa a linha toda — usamos flex para alinhar a imagem dentro */
.image-node-view {
    display: flex;
    width: 100%;
    line-height: 0;
    margin: 0.5rem 0;
}

.image-node-view--resizing {
    user-select: none;
    cursor: nwse-resize;
}

/* Wrapper interno que contém a imagem e o handle */
.image-node-view__inner {
    position: relative;
    display: inline-block;
    line-height: 0;
    border-radius: 6px;
    transition: box-shadow 0.15s;
    max-width: 100%;
}

.image-node-view__inner:hover {
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #6366f1) 40%, transparent);
}

.image-node-view__inner--selected {
    box-shadow: 0 0 0 3px var(--primary-color, #6366f1) !important;
}

.image-node-view__inner img {
    display: block;
    border-radius: 6px;
    max-width: 100%;
    height: auto;
    pointer-events: none;
}

.image-node-view__handle {
    position: absolute;
    bottom: -5px;
    right: -5px;
    width: 14px;
    height: 14px;
    background: var(--primary-color, #6366f1);
    border: 2px solid #fff;
    border-radius: 3px;
    cursor: nwse-resize;
    opacity: 0;
    transition: opacity 0.15s;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.image-node-view__inner:hover .image-node-view__handle,
.image-node-view__inner--selected .image-node-view__handle {
    opacity: 1;
}
</style>
